import { FC, useState } from 'react';
import GenericModal from '../../../shared/GenericModal/GenericModal';
import { ChartErrorState } from '../AthleteInjuryCharts/charts/_shared/ChartErrorState';
import { MetricLineChart } from './charts/MetricLineChart';
import { MetricBarChart } from './charts/MetricBarChart';
import { MetricAcwrChart } from './charts/MetricAcwrChart';
import { useMetricHistory } from './useMetricHistory';
import { DEFAULT_PERIOD, METRIC_CONFIG, MetricKey, MetricPeriod, PERIOD_OPTIONS } from './metricHistory.constants';
import styles from './MetricHistoryModal.module.scss';

interface MetricHistoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  athleteId: string | null;
  athleteName: string | null;
  metric: MetricKey | null;
  /** YYYY-MM-DD — giorno della cella cliccata, evidenziato nel chart */
  highlightDate?: string | null;
}

/**
 * Modale storico di una metrica (sRPE/ACWR/RI/CMJ/HRV/HRrest) condivisa tra
 * Sforzo, Recovery e Readiness: selettore periodo 7/30/90gg, stat avg/min/max,
 * chart Recharts col giorno cliccato evidenziato. Riusa gli stessi endpoint di
 * `useAthleteEffort` (sRPE/ACWR) e `useAthleteReadiness` (RI/CMJ/HRV/HRrest) —
 * nessuna nuova rotta backend.
 */
const MetricHistoryModal: FC<MetricHistoryModalProps> = ({
  isOpen,
  onClose,
  athleteId,
  athleteName,
  metric,
  highlightDate,
}) => {
  const [period, setPeriod] = useState<MetricPeriod>(DEFAULT_PERIOD);

  const { points, stats, isFetching, isError, refetch } = useMetricHistory(
    athleteId,
    metric,
    period,
  );

  if (!metric) return null;

  const config = METRIC_CONFIG[metric];
  const title = athleteName ? `${config.label} — ${athleteName}` : config.label;
  const fmt = (v: number) => `${v.toFixed(config.decimals)}${config.unit ? ` ${config.unit}` : ''}`;

  return (
    <GenericModal isOpen={isOpen} onClose={onClose} title={title} size="xl">
      <div className={styles.root}>
        <div className={styles.periodBar}>
          {PERIOD_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              className={`${styles.periodBtn} ${period === opt.value ? styles.periodBtnActive : ''}`}
              onClick={() => setPeriod(opt.value)}
            >
              {opt.label}
            </button>
          ))}
        </div>

        {isFetching ? (
          <div className={styles.skeleton}>
            <div className={styles.skeletonBar} />
            <div className={`${styles.skeletonBar} ${styles.skeletonBarChart}`} />
          </div>
        ) : isError ? (
          <ChartErrorState onRetry={() => refetch()} />
        ) : (
          <>
            {stats && (
              <div className={styles.statsRow}>
                <div className={styles.statItem}>
                  <span className={styles.statLabel}>Media</span>
                  <span className={styles.statValue} style={{ color: config.accentColor }}>
                    {fmt(stats.avg)}
                  </span>
                </div>
                <div className={styles.statDivider} />
                <div className={styles.statItem}>
                  <span className={styles.statLabel}>Min</span>
                  <span className={styles.statValue}>{fmt(stats.min)}</span>
                </div>
                <div className={styles.statDivider} />
                <div className={styles.statItem}>
                  <span className={styles.statLabel}>Max</span>
                  <span className={styles.statValue}>{fmt(stats.max)}</span>
                </div>
              </div>
            )}

            <div className={styles.chartWrap}>
              {config.chartKind === 'bar' ? (
                <MetricBarChart
                  data={points}
                  label={config.label}
                  unit={config.unit}
                  decimals={config.decimals}
                  color={config.accentColor}
                  highlightDate={highlightDate}
                />
              ) : config.chartKind === 'acwr' ? (
                <MetricAcwrChart data={points} color={config.accentColor} highlightDate={highlightDate} />
              ) : (
                <MetricLineChart
                  data={points}
                  label={config.label}
                  unit={config.unit}
                  decimals={config.decimals}
                  color={config.accentColor}
                  highlightDate={highlightDate}
                />
              )}
            </div>
          </>
        )}
      </div>
    </GenericModal>
  );
};

export default MetricHistoryModal;
